// TODO: split this file into multiple files
import { Box, IconButton } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IDisposable, Univer, UniverInstanceType } from '@univerjs/core';
import '@univerjs/design/lib/index.css';
import '@univerjs/ui/lib/index.css';
import '@univerjs/docs-ui/lib/index.css';
import '@univerjs/sheets-ui/lib/index.css';
import '@univerjs/sheets-formula-ui/lib/index.css';
import '@univerjs/sheets-numfmt-ui/lib/index.css';
import '@univerjs/engine-formula/facade';
import '@univerjs/ui/facade';
import '@univerjs/docs-ui/facade';
import '@univerjs/sheets/facade';
import '@univerjs/sheets-ui/facade';
import '@univerjs/sheets-formula/facade';
import '@univerjs/sheets-numfmt/facade';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import './styles.css';
import { useSnackbar } from '../../context/SnackbarContext';
import {
  useCampaignId,
  useCampaignContextController,
  useSpreadsheetViewType,
  bumpSpreadsheetDataVersion,
  setSpreadsheetBackendSyncPhase,
  type SpreadsheetBackendSyncPhase
} from '../../context/CampaignContext';
import { useTargetPersonaSearch } from '../../context/TargetPersonaSearchContext';
import { CampaignProspectType } from '../../api/campaigns';
import {
  Cell,
  Column,
  displayColumnName,
  EnrichingCellItem,
  getActiveCellExecutorWorkflowId,
  getCells,
  getColumns,
  getEnrichingCells,
  updateColumn
} from '../../api/spreadsheet';
import {
  getActiveProspectPrioritizationWorkflowId,
  getPrioritizingCells
} from '../../api/campaignPrioritization';
import {
  createSpreadsheetCellClickedHandler,
  reapplyValidationStylesForColumnOverrides,
  registerUniverEvents
} from './univerEvents';
import { registerUniverRibbonMenus } from './univerRibbonMenus';
import {
  applyColumnNameOverridesToHeaderState,
  applyColumnSchemaOverridesToHeaderState,
  applyColumnWidthsForIndicesFromSheet,
  applyInitialValidationToUniverCells,
  buildColumnHeaderColumnsCfg,
  colToA1Letter,
  computeColumnWidths,
  deduplicatePrimaryColumns,
  ensureDomainForRowsInPayload as ensureDomainForRowsInPayload,
  getColTypeFromSchema,
  getColumnsWithTypes,
  getColumnEditorInfoForColumn,
  getUniqueColumnName,
  MAX_SPREADSHEET_COLUMNS,
  MAX_SPREADSHEET_ROWS,
  normalizeCellValue,
  normalizeColumnIndices,
  applySheetColumnOrderWithMoveCols,
  sortColumnsForLoad,
  setFaviconLoadedCallback,
  syncSheetCellsToBackend,
  type UniverSheetAPILike
} from './spreadsheetUtils';
import type { ColumnSchemaWithData } from './spreadsheetUtils';
import { appendRowsToSpreadsheetLocal } from './spreadsheetExtControl';
import type { LocalColumnContext } from './spreadsheetExtControl';
import type {
  ColumnHeaderClickInfo,
  EnrichmentUniverApiShape,
  ExecutingCellKeysReadonly,
  SpreadsheetRefHandle,
  SpreadsheetProps
} from './spreadsheetTypes';
import type { SpreadsheetColumnTypeMap } from '../../constants/spreadsheetColumnTypes';
import {
  createEnrichmentHandlers,
  createRunEnrichment,
  filterEnrichmentSelectionToAIColumns,
  forceCellKeysRedraw,
  getFilteredOutRowIds as getFilteredOutRowIdsEnrichment,
  getEnrichmentSelection as getEnrichmentSelectionFromSheet,
  requestSheetRepaint as requestSheetRepaintEnrichment,
  subscribeToActiveEnrichmentWorkflow
} from './spreadsheetEnrichment';
import {
  runProspectPrioritizationFromPanel,
  subscribeToActiveProspectPrioritizationWorkflow
} from './spreadsheetPrioritization';
import {
  cellMatrixFromUniverCells,
  cellValueToUniverCell,
  enrichingApiResponseToCellKeys,
  mapCellsToUniver
} from './spreadsheetUniverUtils';
import { FUniver } from '@univerjs/core/facade';
import {
  CellValidationError,
  formatListValueForDisplay,
  INVALID_CELL_STYLE,
  normalizeListValueForBackend,
  normalizePythonType
} from '../../utils/spreadsheetValidation';
import { Box as MuiBox, Typography, useTheme } from '@mui/material';
import LoadingComponent from '../loading';
import EnrichmentConfirmationDialog from './EnrichmentConfirmationDialog';
import SpreadsheetLockedDialog from './SpreadsheetLockedDialog';
import EnrichmentOverlay, {
  type EnrichmentOverlayVariant
} from './EnrichmentOverlay';
import '@univerjs/sheets-filter-ui/lib/index.css';
import '@univerjs/sheets-filter/facade';
import '@univerjs/sheets-sort-ui/lib/index.css';
import '@univerjs/sheets-sort/facade';
import {
  SYNC_ON_CELL_EDIT_DEBOUNCE_MS,
  UNIVER_SHEETS_UI_MENU_HIDDEN
} from './spreadsheetConstants';
import {
  addDbPriorityCell as addDbPriorityCellToBuffer,
  addSpreadsheetDirtyFullSheet,
  createSyncBuffer
} from './spreadsheetSyncBuffer';
import { resolveCampaignHeaderBackground } from '../../theme/campaignChrome';
import { createSheetSetupAndRenders } from './spreadsheetSheetSetup';
import type { RegisterCellRenderOnLifecycleOptions } from './univerSetup';
import {
  createUniverInstance,
  registerCellRenderOnLifecycle
} from './univerSetup';

export type {
  ColumnHeaderClickInfo,
  SpreadsheetRefHandle,
  SpreadsheetProps
} from './spreadsheetTypes';

const Spreadsheet = forwardRef<SpreadsheetRefHandle, SpreadsheetProps>(
  function Spreadsheet(
    {
      onColumnHeaderClick,
      onColumnDeselect,
      onColumnsChanged,
      columnNameOverrides,
      columnSchemaOverrides,
      onSpreadsheetLockChange,
      onEnrichmentSpreadsheetLockChange,
      onProspectPrioritizationActivityChange
    },
    ref
  ) {
    const campaignId = useCampaignId();
    const [campaignState, dispatch] = useCampaignContextController();
    const savedToIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );
    const applySpreadsheetBackendSyncPhase = useCallback(
      (phase: SpreadsheetBackendSyncPhase) => {
        if (savedToIdleTimerRef.current != null) {
          clearTimeout(savedToIdleTimerRef.current);
          savedToIdleTimerRef.current = null;
        }
        setSpreadsheetBackendSyncPhase(dispatch, phase);
        if (phase === 'saved') {
          savedToIdleTimerRef.current = setTimeout(() => {
            savedToIdleTimerRef.current = null;
            setSpreadsheetBackendSyncPhase(dispatch, 'idle');
          }, 1100);
        }
      },
      [dispatch]
    );
    const spreadsheetBackendSyncPhaseSetterRef = useRef(
      applySpreadsheetBackendSyncPhase
    );
    spreadsheetBackendSyncPhaseSetterRef.current =
      applySpreadsheetBackendSyncPhase;
    const viewType = useSpreadsheetViewType();
    const { canEdit, crmSyncActive, spreadsheetDataVersion } = campaignState;
    const { isSearchRunning: targetPersonaSearchActive } =
      useTargetPersonaSearch();
    const [enrichmentSheetLock, setEnrichmentSheetLock] = useState(false);
    const [activeEnrichmentWorkflowId, setActiveEnrichmentWorkflowId] =
      useState<string | null>(null);
    const [prioritizationSheetLock, setPrioritizationSheetLock] =
      useState(false);
    const [activePrioritizationWorkflowId, setActivePrioritizationWorkflowId] =
      useState<string | null>(null);
    const activePrioritizationWorkflowIdRef = useRef<string | null>(null);
    activePrioritizationWorkflowIdRef.current = activePrioritizationWorkflowId;
    const leadsLockedByTargetPersona =
      viewType === 'lead' && targetPersonaSearchActive;
    const effectiveCanEdit =
      canEdit &&
      !crmSyncActive &&
      !activeEnrichmentWorkflowId &&
      !enrichmentSheetLock &&
      !activePrioritizationWorkflowId &&
      !prioritizationSheetLock &&
      !leadsLockedByTargetPersona;
    /** Like effectiveCanEdit but true during enrichment so Univer allows programmatic updates; we still block user edits via BeforeCommandExecute. */
    const univerEditable =
      canEdit && !crmSyncActive && !leadsLockedByTargetPersona;
    const canEditRef = useRef(effectiveCanEdit);
    canEditRef.current = effectiveCanEdit;
    const prospectPrioritizationActiveRef = useRef(false);
    prospectPrioritizationActiveRef.current =
      !!activePrioritizationWorkflowId || prioritizationSheetLock;
    const dispatchRef = useRef(dispatch);
    dispatchRef.current = dispatch;
    const { showSnackbar } = useSnackbar();
    const showSnackbarRef = useRef(showSnackbar);
    showSnackbarRef.current = showSnackbar;
    const univerContainerRef = useRef<HTMLDivElement>(null);
    const univerRef = useRef<Univer | null>(null);
    const [validationErrors, setValidationErrors] = useState<
      CellValidationError[]
    >([]);
    const [validationErrorsPanelVisible, setValidationErrorsPanelVisible] =
      useState(true);
    const [sheetVisible, setSheetVisible] = useState(false);
    const [cellsLoading, setCellsLoading] = useState(false);
    const [enrichmentModalOpen, setEnrichmentModalOpen] = useState(false);
    const [enrichmentPending, setEnrichmentPending] = useState<{
      row_ids: number[];
      column_names: string[];
      cell_count: number;
    } | null>(null);
    const [sheetLockedModalOpen, setSheetLockedModalOpen] = useState(false);
    const showSheetLockedModalRef = useRef(() => setSheetLockedModalOpen(true));
    showSheetLockedModalRef.current = () => setSheetLockedModalOpen(true);
    const validationErrorsMapRef = useRef<Map<string, CellValidationError>>(
      new Map()
    );
    const setValidationErrorsRef = useRef(setValidationErrors);
    const setSheetVisibleRef = useRef(setSheetVisible);
    const onColumnHeaderClickRef = useRef(onColumnHeaderClick);
    onColumnHeaderClickRef.current = onColumnHeaderClick;
    const onColumnDeselectRef = useRef(onColumnDeselect);
    onColumnDeselectRef.current = onColumnDeselect;
    const onColumnsChangedRef = useRef(onColumnsChanged);
    onColumnsChangedRef.current = onColumnsChanged;
    const onSpreadsheetLockChangeRef = useRef(onSpreadsheetLockChange);
    onSpreadsheetLockChangeRef.current = onSpreadsheetLockChange;
    const onEnrichmentSpreadsheetLockChangeRef = useRef(
      onEnrichmentSpreadsheetLockChange
    );
    onEnrichmentSpreadsheetLockChangeRef.current =
      onEnrichmentSpreadsheetLockChange;
    const onProspectPrioritizationActivityChangeRef = useRef(
      onProspectPrioritizationActivityChange
    );
    onProspectPrioritizationActivityChangeRef.current =
      onProspectPrioritizationActivityChange;
    const columnNameOverridesRef = useRef(columnNameOverrides);
    columnNameOverridesRef.current = columnNameOverrides;
    const columnSchemaOverridesRef = useRef(columnSchemaOverrides);
    columnSchemaOverridesRef.current = columnSchemaOverrides;
    const headerStateRef = useRef<{
      columnNamesByIndex: string[];
      campaignId: string;
      columns: Column[];
      getUniqueColumnName: (base: string, existing: string[]) => string;
      refreshHeaderBar: () => void;
    } | null>(null);
    const univerAPIRef = useRef<ReturnType<typeof FUniver.newAPI> | null>(null);
    const cellRenderDisposableRef = useRef<IDisposable | null>(null);
    const columnHeaderExtensionDisposableRef = useRef<IDisposable | null>(null);
    const isApplyingValidationStylesRef = useRef(false);
    /** Set while applying enrichment stream results to sheet; prevents change handlers from echoing these updates back to the backend. */
    const isApplyingEnrichmentCellRef = useRef(false);
    /** Set while sync is applying backend cells to sheet; prevents mutation handler from scheduling another sync (stops add/remove loop). */
    const isApplyingSyncFromBackendRef = useRef(false);
    /** Nested `applyPrioritizationResultFromBackend` calls share one sync guard so inner finally does not clear the ref early. */
    const prioritizationApplyDepthRef = useRef(0);
    /** When true, runSpreadsheetSync skips putCells/deleteCells so we do not write to backend during enrichment (e.g. on refresh). */
    const enrichmentLockedRef = useRef(false);
    /** When true, insert-col BeforeCommandExecute skips splicing default names (caller already set names from backend). */
    const skipInsertColBeforeRef = useRef(false);
    /** When true, insert-col handler skips createColumn (used when adding columns from backend e.g. prioritization). */
    const skipInsertColCreateRef = useRef(false);
    /** True while programmatic move-cols applies GET /columns order; avoids marking every cell spreadsheet-dirty (would make merge prefer stale sheet over DB). */
    const applyBackendColumnReorderInProgressRef = useRef(false);
    const enrichmentStreamCancelRef = useRef<(() => void) | null>(null);
    const prioritizationStreamCancelRef = useRef<(() => void) | null>(null);
    /** True while panel start awaits fetchRowIds / apply-from-backend before lock; prevents duplicate POST. */
    const prospectPrioritizationPanelStartInFlightRef = useRef(false);
    /** Debounce full-sheet sync after cell edits (1s after user stops typing). */
    const syncDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );
    /** Set by registerUniverEvents; enrichment stream calls this when backend sends sync_required. */
    const scheduleDebouncedSyncRef = useRef<(() => void) | null>(null);
    const dbTriggeredSyncPendingRef = useRef(false);
    /** Buffer for spreadsheet/DB priority merge; cleared after each sync. */
    const syncBufferRef = useRef(createSyncBuffer());
    /** Serialize row insert/remove so concurrent operations don't overwrite each other. */
    const rowOperationPromiseRef = useRef<Promise<void>>(Promise.resolve());
    /** Serialize column add/remove so concurrent operations don't overwrite each other. */
    const columnOperationPromiseRef = useRef<Promise<void>>(Promise.resolve());
    /** Prevent rapid Add Column clicks from creating duplicate column names. */
    const addColumnInProgressRef = useRef(false);
    /** Prevent rapid Add Row clicks from creating duplicate row ids. */
    const addRowInProgressRef = useRef(false);
    const onEnrichmentClickRef = useRef<() => void>(() => {});
    const onEnrichAllClickRef = useRef<() => void>(() => {});
    const onDeduplicateClickRef = useRef<() => void | Promise<void>>(() => {});
    const onBumpSpreadsheetDataVersionRef = useRef<() => void>(() => {});
    /** Cells currently being prioritized (for overlay + blue highlight). */
    const prioritizingCellsRef = useRef<Set<string>>(new Set());
    const [prioritizingCellsCount, setPrioritizingCellsCount] = useState(0);
    /** Cells currently being enriched (for overlay). */
    const enrichingCellsRef = useRef<Set<string>>(new Set());
    const [enrichingCellsCount, setEnrichingCellsCount] = useState(0);
    /** Stable `{ has }` over enriching ∪ prioritizing — merged Set per cell paint caused GC churn (Univer calls highlight draw per visible cell). */
    const executingCellKeysReadonlyRef = useRef<ExecutingCellKeysReadonly | null>(
      null
    );
    if (executingCellKeysReadonlyRef.current === null) {
      executingCellKeysReadonlyRef.current = {
        has(key: string): boolean {
          return (
            enrichingCellsRef.current.has(key) ||
            prioritizingCellsRef.current.has(key)
          );
        }
      };
    }
    const theme = useTheme();
    const customColors = (
      theme as { customColors?: Record<string, string | undefined> }
    ).customColors;
    const themePrimary = customColors?.themePrimary ?? '#49BAFD';

    /** Aligns primary column header accent with campaign page chrome (account / lead / opportunity). */
    const primaryColumnHeaderBgRef = useRef('#E8F4FC');
    primaryColumnHeaderBgRef.current =
      resolveCampaignHeaderBackground(
        {
          campaignChromeAccountBg:
            customColors?.campaignChromeAccountBg ?? '#E8F4FC',
          campaignChromeLeadBg: customColors?.campaignChromeLeadBg ?? '#F1F3F4',
          campaignChromeOpportunityBg:
            customColors?.campaignChromeOpportunityBg ?? '#E4DCF2'
        },
        campaignState.currentCampaign?.prospect_type,
        viewType
      ) ?? '#E8F4FC';

    /** Refs so `refreshHeaderBar` (set once at Univer init) always sees current view/theme. */
    const spreadsheetViewTypeForHeaderRef = useRef(viewType);
    spreadsheetViewTypeForHeaderRef.current = viewType;
    const campaignChromeAccountBgForLeadPrimaryRef = useRef(
      customColors?.campaignChromeAccountBg ?? '#E8F4FC'
    );
    campaignChromeAccountBgForLeadPrimaryRef.current =
      customColors?.campaignChromeAccountBg ?? '#E8F4FC';
    const prioritizationColumnHeaderBgForHeaderRef = useRef(
      customColors?.spreadsheetPrioritizationHeaderBg ?? '#E4DCF2'
    );
    prioritizationColumnHeaderBgForHeaderRef.current =
      customColors?.spreadsheetPrioritizationHeaderBg ?? '#E4DCF2';

    /** Re-apply primary / prioritization header fills when campaign chrome or theme tokens change. */
    useEffect(() => {
      headerStateRef.current?.refreshHeaderBar?.();
    }, [
      customColors?.campaignChromeAccountBg,
      customColors?.campaignChromeLeadBg,
      customColors?.campaignChromeOpportunityBg,
      customColors?.spreadsheetPrioritizationHeaderBg,
      viewType,
      campaignState.currentCampaign?.prospect_type
    ]);

    /** Enrichment or prioritization: skip risky writes/sync from the sheet layer. */
    const spreadsheetJobLocked = !!(
      activeEnrichmentWorkflowId ||
      enrichmentSheetLock ||
      activePrioritizationWorkflowId ||
      prioritizationSheetLock
    );
    enrichmentLockedRef.current = spreadsheetJobLocked;
    useEffect(() => {
      onSpreadsheetLockChangeRef.current?.(spreadsheetJobLocked);
    }, [spreadsheetJobLocked]);

    const enrichmentLocksSpreadsheet = !!(
      activeEnrichmentWorkflowId || enrichmentSheetLock
    );
    useEffect(() => {
      onEnrichmentSpreadsheetLockChangeRef.current?.(
        enrichmentLocksSpreadsheet
      );
    }, [enrichmentLocksSpreadsheet]);

    const prioritizationActivityActive = !!(
      activePrioritizationWorkflowId || prioritizationSheetLock
    );
    useEffect(() => {
      onProspectPrioritizationActivityChangeRef.current?.(
        prioritizationActivityActive
      );
    }, [prioritizationActivityActive]);

    /** Apply read-only / editable. Use univerEditable for setEditable so during enrichment Univer still allows programmatic updates; use effectiveCanEdit for selection/abort so user cannot interact. */
    useEffect(() => {
      const api = univerAPIRef.current;
      const fWorkbook = api?.getActiveWorkbook?.();
      if (!fWorkbook) return;
      if (typeof fWorkbook.setEditable === 'function') {
        fWorkbook.setEditable(univerEditable);
      }
      if (effectiveCanEdit) {
        if (typeof fWorkbook.enableSelection === 'function')
          fWorkbook.enableSelection();
      } else {
        if (typeof fWorkbook.disableSelection === 'function')
          fWorkbook.disableSelection();
        if (
          typeof (fWorkbook as { abortEditingAsync?: () => Promise<boolean> })
            .abortEditingAsync === 'function'
        ) {
          (fWorkbook as { abortEditingAsync: () => Promise<boolean> })
            .abortEditingAsync()
            .catch(() => {});
        }
      }
    }, [effectiveCanEdit, univerEditable, sheetVisible]);

    setValidationErrorsRef.current = setValidationErrors;
    setSheetVisibleRef.current = setSheetVisible;

    const requestSheetRepaint = useCallback(() => {
      requestSheetRepaintEnrichment({
        getUniver: () =>
          univerRef.current as { __getInjector(): unknown } | null,
        getApi: () => univerAPIRef.current
      });
    }, []);

    const applyPrioritizationResultFromBackend =
      useCallback(async (): Promise<void> => {
        const cid = campaignId;
        const api = univerAPIRef.current;
        const state = headerStateRef.current;
        if (!cid || !api || !state?.columnNamesByIndex?.length) return;
        prioritizationApplyDepthRef.current += 1;
        isApplyingSyncFromBackendRef.current = true;
        try {
          const currentNames = new Set(state.columnNamesByIndex);
          const [columnsRes, { cells: backendCells }] = await Promise.all([
            getColumns(cid, viewType),
            getCells(cid, viewType)
          ]);
          const rawColumns =
            (columnsRes as { columns?: Column[] })?.columns ?? columnsRes;
          const columnsArray = Array.isArray(rawColumns) ? rawColumns : [];
          const sortedColumns = columnsArray.sort(
            (a: Column, b: Column) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
          );
          const newColumns = sortedColumns.filter(
            (c: Column) => !currentNames.has(c.col_name)
          );
          const newColumnNames = new Set(
            newColumns.map((c: Column) => c.col_name)
          );
          if (newColumns.length === 0 && (backendCells ?? []).length === 0)
            return;
          const columnsLimited = sortedColumns.slice(
            0,
            MAX_SPREADSHEET_COLUMNS
          );
          const columnNamesSet = new Set(
            columnsLimited.map((c: Column) => c.col_name)
          );
          const cellsToApply = (backendCells ?? []).filter(
            (c: Cell) =>
              c.row_id < MAX_SPREADSHEET_ROWS &&
              columnNamesSet.has(c.column_name) &&
              (newColumnNames.size === 0 || newColumnNames.has(c.column_name))
          );
          if (newColumns.length > 0) {
            try {
              for (const col of newColumns) {
                const idx = state.columnNamesByIndex.length;
                state.columnNamesByIndex.push(col.col_name);
                if (state.columns)
                  state.columns.push(col as ColumnSchemaWithData);
                skipInsertColBeforeRef.current = true;
                skipInsertColCreateRef.current = true;
                addColumnInProgressRef.current = true;
                api.executeCommand('sheet.command.insert-col', {
                  range: { startColumn: idx, endColumn: idx }
                });
              }
            } finally {
              addColumnInProgressRef.current = false;
            }
            await new Promise<void>((resolve) =>
              requestAnimationFrame(() => resolve())
            );
            state.refreshHeaderBar?.();
            await new Promise<void>((resolve) =>
              requestAnimationFrame(() => resolve())
            );
          }

          if (cellsToApply.length > 0) {
            for (const cell of cellsToApply) {
              const colIndex = state.columnNamesByIndex.indexOf(
                cell.column_name
              );
              if (colIndex < 0) continue;
              const range = {
                startRow: cell.row_id,
                endRow: cell.row_id,
                startColumn: colIndex,
                endColumn: colIndex
              };
              let rawVal = normalizeCellValue(cell.cell_value ?? '');
              const colType = state.columns[colIndex]
                ? getColTypeFromSchema(
                    state.columns[colIndex] as ColumnSchemaWithData
                  )
                : undefined;
              if (normalizePythonType(colType) === 'list')
                rawVal = formatListValueForDisplay(rawVal);
              const value = {
                [cell.row_id]: {
                  [colIndex]: cellValueToUniverCell(rawVal, { colType })
                }
              };
              api.executeCommand('sheet.command.set-range-values', {
                range,
                value
              });
              addDbPriorityCellToBuffer(
                syncBufferRef.current,
                cell.row_id,
                cell.column_name
              );
            }
            await new Promise<void>((resolve) =>
              requestAnimationFrame(() => resolve())
            );
          }
          dbTriggeredSyncPendingRef.current = true;
          if (syncDebounceTimerRef.current == null)
            scheduleDebouncedSyncRef.current?.();
        } finally {
          prioritizationApplyDepthRef.current -= 1;
          if (prioritizationApplyDepthRef.current <= 0) {
            prioritizationApplyDepthRef.current = 0;
            isApplyingSyncFromBackendRef.current = false;
          }
        }
      }, [campaignId, viewType]);

    const ensurePrioritizationOutputColumnsOnSheet = useCallback(
      async (outputColumnNames: string[]): Promise<void> => {
        const api = univerAPIRef.current;
        const state = headerStateRef.current;
        if (!api || !state?.columnNamesByIndex?.length) return;
        const uniqueNames = Array.from(new Set(outputColumnNames));
        if (uniqueNames.length === 0) return;

        const existing = new Set(state.columnNamesByIndex);
        const toAdd = uniqueNames.filter((name) => !existing.has(name));
        if (toAdd.length === 0) return;

        prioritizationApplyDepthRef.current += 1;
        isApplyingSyncFromBackendRef.current = true;
        try {
          try {
            for (const colName of toAdd) {
              const idx = state.columnNamesByIndex.length;
              state.columnNamesByIndex.push(colName);
              if (state.columns) {
                state.columns.push({
                  col_name: colName,
                  col_idx: idx,
                  col_type: 'str',
                  description: ''
                } as ColumnSchemaWithData);
              }
              skipInsertColBeforeRef.current = true;
              skipInsertColCreateRef.current = true;
              addColumnInProgressRef.current = true;
              api.executeCommand('sheet.command.insert-col', {
                range: { startColumn: idx, endColumn: idx }
              });
            }
          } finally {
            addColumnInProgressRef.current = false;
          }
          await new Promise<void>((resolve) =>
            requestAnimationFrame(() => resolve())
          );
          state.refreshHeaderBar?.();
          await new Promise<void>((resolve) =>
            requestAnimationFrame(() => resolve())
          );
        } finally {
          prioritizationApplyDepthRef.current -= 1;
          if (prioritizationApplyDepthRef.current <= 0) {
            prioritizationApplyDepthRef.current = 0;
            isApplyingSyncFromBackendRef.current = false;
          }
        }
      },
      []
    );

    const applyPrioritizationLatestRef = useRef(
      applyPrioritizationResultFromBackend
    );
    applyPrioritizationLatestRef.current = applyPrioritizationResultFromBackend;

    const flushPrioritizingHighlights = useCallback(() => {
      forceCellKeysRedraw(prioritizingCellsRef.current, {
        getApi: () => univerAPIRef.current as EnrichmentUniverApiShape | null,
        getHeaderState: () => headerStateRef.current,
        cellValueToUniverCell,
        isApplyingEnrichmentCellRef
      });
      requestSheetRepaint();
    }, [requestSheetRepaint]);

    const syncPrioritizingCellsForWorkflow = useCallback(
      async (
        workflowId: string,
        options?: { mergeSheetFromBackend?: boolean }
      ) => {
        const cid = campaignId;
        if (!cid || !workflowId) return;
        if (options?.mergeSheetFromBackend) {
          await applyPrioritizationResultFromBackend();
        }
        const state = headerStateRef.current;
        if (!state?.columnNamesByIndex?.length) return;
        const items = await getPrioritizingCells(cid, workflowId, viewType);
        const keys = enrichingApiResponseToCellKeys(
          items,
          state.columnNamesByIndex
        );
        prioritizingCellsRef.current = keys;
        setPrioritizingCellsCount(keys.size);
        flushPrioritizingHighlights();
      },
      [
        campaignId,
        viewType,
        applyPrioritizationResultFromBackend,
        flushPrioritizingHighlights
      ]
    );

    /** Column editor info for the first column in the current selection, or null if no selection. */
    useImperativeHandle(
      ref,
      () => ({
        getColumnEditorInfoForSelection(): ColumnHeaderClickInfo | null {
          try {
            const api = univerAPIRef.current;
            const state = headerStateRef.current;
            if (!api || !state?.columnNamesByIndex?.length) return null;
            const activeRange = api
              .getActiveWorkbook()
              ?.getActiveSheet()
              ?.getActiveRange?.();
            const rangeData =
              typeof activeRange?.getRange === 'function'
                ? activeRange.getRange()
                : null;
            if (
              !rangeData ||
              typeof rangeData !== 'object' ||
              !('startColumn' in rangeData)
            )
              return null;
            const range = rangeData as {
              startRow: number;
              endRow: number;
              startColumn: number;
              endColumn: number;
            };
            const startCol = Math.max(0, range.startColumn);
            if (startCol >= state.columnNamesByIndex.length) return null;
            return getColumnEditorInfoForColumn(startCol, state);
          } catch {
            return null;
          }
        },
        getFilteredOutRowIds(): number[] | null {
          return getFilteredOutRowIdsEnrichment(univerAPIRef.current);
        },
        getEnrichmentSelection() {
          const raw = getEnrichmentSelectionFromSheet(
            univerAPIRef.current,
            headerStateRef.current
          );
          if (!raw) return null;
          const state = headerStateRef.current;
          if (!state) return null;
          return (
            filterEnrichmentSelectionToAIColumns(
              raw,
              state,
              columnSchemaOverrides ?? {}
            ) ?? null
          );
        },
        applyPrioritizationResultFromBackend,
        async startProspectPrioritization(
          fetchRowIds: () => Promise<number[]>,
          outputColumnNames: string[]
        ): Promise<void> {
          if (!campaignId) return;
          if (prioritizationStreamCancelRef.current) {
            return;
          }
          if (prospectPrioritizationPanelStartInFlightRef.current) {
            return;
          }
          prospectPrioritizationPanelStartInFlightRef.current = true;
          try {
            await runProspectPrioritizationFromPanel(
              campaignId,
              viewType,
              fetchRowIds,
              outputColumnNames,
              {
                getHeaderState: () => headerStateRef.current,
                prioritizingCellsRef,
                setPrioritizingCellsCount,
                setPrioritizationSheetLock,
                setActivePrioritizationWorkflowId,
                prioritizationStreamCancelRef,
                showSnackbar: (
                  msg: string,
                  severity: 'error' | 'warning' | 'info' | 'success'
                ) => showSnackbarRef.current?.(msg, severity),
                requestSheetRepaint,
                completePrioritization: () =>
                  applyPrioritizationLatestRef.current(),
                syncPrioritizingCellsForWorkflow,
                applyPrioritizationResultFromBackend,
                ensurePrioritizationOutputColumnsOnSheet,
                flushPrioritizingHighlights
              }
            );
          } finally {
            prospectPrioritizationPanelStartInFlightRef.current = false;
          }
        },
        runSpreadsheetSync(): void {
          dbTriggeredSyncPendingRef.current = true;
          if (syncDebounceTimerRef.current == null)
            scheduleDebouncedSyncRef.current?.();
        },
        appendRowsToSpreadsheetLocal(
          uploadedRows: Record<string, unknown>[],
          customColumnTypes?: SpreadsheetColumnTypeMap
        ): number {
          const api = univerAPIRef.current;
          const state = headerStateRef.current;
          if (!api || !state) return 0;
          const ctx: LocalColumnContext = {
            univerAPI: api,
            headerStateRef,
            skipInsertColBeforeRef,
            skipInsertColCreateRef,
            cellValueToUniverCell
          };
          return appendRowsToSpreadsheetLocal(
            ctx,
            uploadedRows,
            customColumnTypes
          );
        }
      }),
      [
        campaignId,
        viewType,
        columnSchemaOverrides,
        applyPrioritizationResultFromBackend,
        ensurePrioritizationOutputColumnsOnSheet,
        syncPrioritizingCellsForWorkflow,
        flushPrioritizingHighlights
      ]
    );

    const runEnrichment = createRunEnrichment(() => ({
      campaignId,
      viewType,
      runCtx: {
        getUniver: () =>
          univerRef.current as { __getInjector(): unknown } | null,
        getApi: () => univerAPIRef.current,
        getHeaderState: () => headerStateRef.current,
        cellValueToUniverCell,
        enrichingCellsRef,
        setEnrichingCellsCount,
        setEnrichmentModalOpen,
        setEnrichmentPending,
        setActiveEnrichmentWorkflowId,
        setEnrichmentSheetLock,
        enrichmentStreamCancelRef,
        isApplyingEnrichmentCellRef,
        showSnackbar,
        requestSheetRepaint,
        addDbPriorityCell: (rowId, columnName) => {
          if (syncBufferRef.current) {
            addDbPriorityCellToBuffer(syncBufferRef.current, rowId, columnName);
          }
        },
        requestSync: () => {
          dbTriggeredSyncPendingRef.current = true;
          if (syncDebounceTimerRef.current == null)
            scheduleDebouncedSyncRef.current?.();
        },
        requestSheetRefresh: () =>
          bumpSpreadsheetDataVersion(dispatchRef.current)
      }
    }));

    /** On mount/refresh, reconnect to active enrichment workflow stream if backend has one.
     * Deps are only campaignId and viewType so that bumping spreadsheetDataVersion (full refresh)
     * does not re-run this effect and cancel the stream; showSnackbar is read via ref when needed. */
    useEffect(() => {
      const reconnectCtx = {
        getApi: () => univerAPIRef.current,
        getHeaderState: () => headerStateRef.current,
        cellValueToUniverCell,
        setActiveEnrichmentWorkflowId,
        enrichmentStreamCancelRef,
        enrichingCellsRef,
        setEnrichingCellsCount,
        isApplyingEnrichmentCellRef,
        showSnackbar: (
          msg: string,
          severity: 'error' | 'warning' | 'info' | 'success'
        ) => showSnackbarRef.current?.(msg, severity),
        requestSheetRepaint,
        addDbPriorityCell: (rowId: number, columnName: string) => {
          if (syncBufferRef.current) {
            addDbPriorityCellToBuffer(syncBufferRef.current, rowId, columnName);
          }
        },
        requestSync: () => {
          dbTriggeredSyncPendingRef.current = true;
          if (syncDebounceTimerRef.current == null)
            scheduleDebouncedSyncRef.current?.();
        },
        requestSheetRefresh: () =>
          bumpSpreadsheetDataVersion(dispatchRef.current)
      };
      return subscribeToActiveEnrichmentWorkflow(
        campaignId,
        viewType,
        reconnectCtx
      );
    }, [campaignId, viewType]);

    useEffect(() => {
      const reconnectCtx = {
        getActivePrioritizationWorkflowId: () =>
          activePrioritizationWorkflowIdRef.current,
        setActivePrioritizationWorkflowId,
        prioritizationStreamCancelRef,
        prioritizingCellsRef,
        setPrioritizingCellsCount,
        setPrioritizationSheetLock,
        showSnackbar: (
          msg: string,
          severity: 'error' | 'warning' | 'info' | 'success'
        ) => showSnackbarRef.current?.(msg, severity),
        requestSheetRepaint,
        completePrioritization: () => applyPrioritizationLatestRef.current(),
        syncPrioritizingCellsForWorkflow
      };
      return subscribeToActiveProspectPrioritizationWorkflow(
        campaignId,
        viewType,
        reconnectCtx
      );
    }, [
      campaignId,
      viewType,
      requestSheetRepaint,
      syncPrioritizingCellsForWorkflow
    ]);

    const enrichmentHandlers = createEnrichmentHandlers({
      getApi: () => univerAPIRef.current as UniverSheetAPILike | null,
      getHeaderState: () => headerStateRef.current,
      getColumnSchemaOverrides: () => columnSchemaOverridesRef.current,
      activeEnrichmentWorkflowId,
      enrichmentSheetLock,
      activePrioritizationWorkflowId,
      prioritizationSheetLock,
      enrichmentPending,
      showSnackbar,
      setEnrichmentModalOpen,
      setEnrichmentPending,
      setActiveEnrichmentWorkflowId,
      runEnrichment
    });
    onEnrichmentClickRef.current = enrichmentHandlers.handleEnrichmentClick;
    onBumpSpreadsheetDataVersionRef.current = () =>
      bumpSpreadsheetDataVersion(dispatchRef.current);
    onEnrichAllClickRef.current = enrichmentHandlers.handleEnrichAllClick;

    const handleDeduplicateClick = React.useCallback(() => {
      if (!campaignId) return;
      const api = univerAPIRef.current;
      const state = headerStateRef.current;
      const columnNamesByIndex = state?.columnNamesByIndex ?? [];
      if (!columnNamesByIndex.length || !api || !state?.columns) {
        showSnackbarRef.current?.('Sheet has no columns.', 'warning');
        return;
      }
      const sheet = api.getActiveWorkbook()?.getActiveSheet()?.getSheet();
      if (!sheet || typeof sheet.getCellValueOnly !== 'function') return;
      const rowCount = sheet.getConfig?.()?.rowCount ?? 0;
      const cols = columnNamesByIndex.length;
      if (rowCount <= 0 || cols <= 0) return;

      showSnackbarRef.current?.('Deduplicating…', 'success');
      try {
        const cells: Cell[] = [];
        for (let r = 0; r < rowCount; r++) {
          for (let c = 0; c < cols; c++) {
            const columnName = columnNamesByIndex[c];
            const v = sheet.getCellValueOnly(r, c);
            let cellValue = normalizeCellValue(v);
            const colType = state.columns[c]
              ? getColTypeFromSchema(state.columns[c] as ColumnSchemaWithData)
              : undefined;
            if (normalizePythonType(colType) === 'list') {
              cellValue = normalizeListValueForBackend(cellValue);
            }
            cells.push({
              row_id: r,
              column_name: columnName,
              cell_value: cellValue
            });
          }
        }
        const isOpportunityCampaign =
          campaignState.currentCampaign?.prospect_type ===
          CampaignProspectType.OPPORTUNITY;
        const dedupeOptions =
          isOpportunityCampaign && columnNamesByIndex.length > 0
            ? { primaryColumnNamesOverride: [columnNamesByIndex[0]] }
            : undefined;
        const result = deduplicatePrimaryColumns(
          cells,
          columnNamesByIndex,
          dedupeOptions
        );
        if (result.primaryColumnsInSheet.length === 0) {
          showSnackbarRef.current?.(
            isOpportunityCampaign
              ? 'Sheet has no columns.'
              : 'No primary columns (Domain, Lead, Company) found. Add at least one to deduplicate.',
            'warning'
          );
          return;
        }
        if (result.removedRowCount <= 0) {
          showSnackbarRef.current?.('No duplicate rows found.', 'info');
          return;
        }
        const newRowCount = result.cells.length / columnNamesByIndex.length;
        if (result.cells.length > 0) {
          const valueObj = mapCellsToUniver(state.columns, result.cells);
          const maxR = newRowCount - 1;
          const maxC = columnNamesByIndex.length - 1;
          isApplyingSyncFromBackendRef.current = true;
          try {
            if (maxR >= 0 && maxC >= 0) {
              api.executeCommand('sheet.command.set-range-values', {
                range: {
                  startRow: 0,
                  startColumn: 0,
                  endRow: maxR,
                  endColumn: maxC
                },
                value: valueObj
              });
            }
            const sheet = api.getActiveWorkbook()?.getActiveSheet()?.getSheet();
            const currentRowCount = sheet?.getConfig?.()?.rowCount ?? 0;
            if (currentRowCount > newRowCount) {
              api.executeCommand('sheet.command.remove-row', {
                range: { startRow: newRowCount, endRow: currentRowCount - 1 }
              });
            }
            addSpreadsheetDirtyFullSheet(
              syncBufferRef.current,
              columnNamesByIndex,
              newRowCount
            );
            scheduleDebouncedSyncRef.current?.();
          } finally {
            queueMicrotask(() => {
              isApplyingSyncFromBackendRef.current = false;
            });
          }
        }

        showSnackbarRef.current?.(
          `Removed ${result.removedRowCount} duplicate row(s).`,
          'info'
        );
      } catch (e) {
        const message =
          e instanceof Error
            ? (e.message ?? 'Deduplication failed.')
            : 'Deduplication failed.';
        showSnackbarRef.current?.(message, 'error');
      }
    }, [campaignId, campaignState.currentCampaign?.prospect_type]);
    onDeduplicateClickRef.current = handleDeduplicateClick;

    useEffect(() => {
      if (!campaignId) {
        return;
      }
      if (!univerContainerRef.current) {
        return;
      }
      let cancelled = false;
      applySpreadsheetBackendSyncPhase('idle');
      setSheetVisible(false);
      setCellsLoading(true);
      const getSheetData = async () => {
        const columnsRes = await getColumns(campaignId, viewType);
        const columnsArray = Array.isArray(columnsRes?.columns)
          ? columnsRes.columns
          : [];
        const sortedColumns = columnsArray.sort(
          (a: Column, b: Column) => (a.col_idx ?? 0) - (b.col_idx ?? 0)
        );
        const [cellsRes, active, activePrio] = await Promise.all([
          getCells(campaignId, viewType),
          getActiveCellExecutorWorkflowId(campaignId, viewType),
          getActiveProspectPrioritizationWorkflowId(campaignId, viewType)
        ]);
        let enrichingCells: EnrichingCellItem[] = [];
        let prioritizingCells: EnrichingCellItem[] = [];
        const prioWf = activePrio?.workflow_id ?? null;
        const enrichPromise = active?.workflow_id
          ? getEnrichingCells(campaignId, active.workflow_id, viewType)
          : Promise.resolve([]);
        const prioPromise = prioWf
          ? getPrioritizingCells(campaignId, prioWf, viewType)
          : Promise.resolve([]);
        [enrichingCells, prioritizingCells] = await Promise.all([
          enrichPromise,
          prioPromise
        ]);
        return {
          columns: sortedColumns,
          cells: cellsRes.cells ?? [],
          enrichingCells: enrichingCells ?? [],
          prioritizingCells: prioritizingCells ?? []
        };
      };
      getSheetData()
        .then(
          ({ columns, cells, enrichingCells, prioritizingCells }) => {
            if (cancelled) return;
            // Enforce max rows/columns: only display and edit within limits
            let columnsLimited = columns.slice(0, MAX_SPREADSHEET_COLUMNS);
            columnsLimited = sortColumnsForLoad(columnsLimited);
            // Normalize in-memory col_idx to match array index for local consistency (order is backend-owned).
            normalizeColumnIndices(columnsLimited);
            const columnNamesSet = new Set(
              columnsLimited.map((c: Column) => c.col_name)
            );
            const cellsLimited = cells.filter(
              (c: Cell) =>
                c.row_id < MAX_SPREADSHEET_ROWS &&
                columnNamesSet.has(c.column_name)
            );

            const univerCells = mapCellsToUniver(columnsLimited, cellsLimited);
            // Own copy of index → column name (for API sync and custom column header labels)
            const columnNamesByIndex: string[] = columnsLimited.map(
              (c: Column) => c.col_name
            );
            headerStateRef.current = {
              columnNamesByIndex,
              campaignId,
              columns: columnsLimited,
              getUniqueColumnName,
              refreshHeaderBar: () => {}
            };
            const columnsWithTypes = getColumnsWithTypes(columnsLimited);
            const cellMatrix = cellMatrixFromUniverCells(univerCells);
            const initialErrors = applyInitialValidationToUniverCells(
              columnsWithTypes,
              cellMatrix
            );
            validationErrorsMapRef.current = new Map(
              initialErrors.map((e) => [`${e.row},${e.col}`, e])
            );
            setValidationErrorsRef.current(initialErrors);

            cellRenderDisposableRef.current?.dispose();
            cellRenderDisposableRef.current = null;
            columnHeaderExtensionDisposableRef.current?.dispose();
            columnHeaderExtensionDisposableRef.current = null;
            univerRef.current = null;
            setFaviconLoadedCallback(null);
            enrichingCellsRef.current = new Set();
            setEnrichingCellsCount(0);
            prioritizingCellsRef.current = new Set();
            setPrioritizingCellsCount(0);
            const keys = enrichingApiResponseToCellKeys(
              enrichingCells,
              columnNamesByIndex
            );
            enrichingCellsRef.current = keys;
            setEnrichingCellsCount(keys.size);
            const prioKeys = enrichingApiResponseToCellKeys(
              prioritizingCells,
              columnNamesByIndex
            );
            prioritizingCellsRef.current = prioKeys;
            setPrioritizingCellsCount(prioKeys.size);
            const { univer, api: univerAPI } = createUniverInstance({
              container: univerContainerRef.current!,
              menuHidden: UNIVER_SHEETS_UI_MENU_HIDDEN
            });
            univerRef.current = univer;
            univerAPIRef.current = univerAPI;

            const ensureDomainForRows = (
              cells: Cell[],
              columnNamesByIndex: string[],
              api: ReturnType<typeof FUniver.newAPI>
            ): Cell[] => {
              const getCellValue = (row: number, col: number) => {
                const sheet = api
                  .getActiveWorkbook()
                  ?.getActiveSheet?.()
                  ?.getSheet?.();
                return sheet ? sheet.getCellValueOnly(row, col) : null;
              };
              return ensureDomainForRowsInPayload(
                cells,
                columnNamesByIndex,
                getCellValue
              );
            };

            const onBackendOnlyColumns = async (
              backendOnlyColumns: Column[],
              backendCells: Cell[]
            ) => {
              const state = headerStateRef.current;
              const api = univerAPIRef.current;
              if (!state || !api) return;
              const newColumnNames = new Set(
                backendOnlyColumns.map((c: Column) => c.col_name)
              );
              const cellsToApply = backendCells.filter(
                (c: Cell) =>
                  newColumnNames.has(c.column_name) &&
                  c.row_id < MAX_SPREADSHEET_ROWS
              );
              isApplyingSyncFromBackendRef.current = true;
              try {
                for (const col of backendOnlyColumns) {
                  const idx = state.columnNamesByIndex.length;
                  state.columnNamesByIndex.push(col.col_name);
                  if (state.columns)
                    state.columns.push(col as ColumnSchemaWithData);
                  skipInsertColBeforeRef.current = true;
                  skipInsertColCreateRef.current = true;
                  addColumnInProgressRef.current = true;
                  api.executeCommand('sheet.command.insert-col', {
                    range: { startColumn: idx, endColumn: idx }
                  });
                }
                addColumnInProgressRef.current = false;
              } finally {
                isApplyingSyncFromBackendRef.current = false;
              }
              await new Promise<void>((r) => requestAnimationFrame(() => r()));
              state.refreshHeaderBar?.();
              await new Promise<void>((r) => requestAnimationFrame(() => r()));
              isApplyingSyncFromBackendRef.current = true;
              try {
                for (const cell of cellsToApply) {
                  const colIndex = state.columnNamesByIndex.indexOf(
                    cell.column_name
                  );
                  if (colIndex < 0) continue;
                  const range = {
                    startRow: cell.row_id,
                    endRow: cell.row_id,
                    startColumn: colIndex,
                    endColumn: colIndex
                  };
                  let rawVal = normalizeCellValue(cell.cell_value ?? '');
                  const colType = state.columns[colIndex]
                    ? getColTypeFromSchema(
                        state.columns[colIndex] as ColumnSchemaWithData
                      )
                    : undefined;
                  if (normalizePythonType(colType) === 'list')
                    rawVal = formatListValueForDisplay(rawVal);
                  const value = {
                    [cell.row_id]: {
                      [colIndex]: cellValueToUniverCell(rawVal, { colType })
                    }
                  };
                  api.executeCommand('sheet.command.set-range-values', {
                    range,
                    value
                  });
                  addDbPriorityCellToBuffer(
                    syncBufferRef.current,
                    cell.row_id,
                    cell.column_name
                  );
                }
              } finally {
                isApplyingSyncFromBackendRef.current = false;
              }
              const colsToResize = new Set<number>();
              for (const cell of cellsToApply) {
                const idx = state.columnNamesByIndex.indexOf(cell.column_name);
                if (idx >= 0) colsToResize.add(idx);
              }
              if (colsToResize.size > 0) {
                const columnNames = state.columnNamesByIndex;
                const cols = state.columns;
                requestAnimationFrame(() => {
                  applyColumnWidthsForIndicesFromSheet(
                    api,
                    columnNames,
                    cols,
                    Array.from(colsToResize)
                  );
                });
              }
            };

            const applySheetColumnOrderFromBackend = async (
              targetNames: string[]
            ) => {
              applyBackendColumnReorderInProgressRef.current = true;
              try {
                await applySheetColumnOrderWithMoveCols(
                  univerAPI,
                  headerStateRef,
                  targetNames
                );
              } finally {
                applyBackendColumnReorderInProgressRef.current = false;
              }
            };

            registerUniverRibbonMenus({
              univerAPI,
              themePrimary,
              campaignId,
              viewType,
              canEditRef,
              onEnrichmentClickRef,
              onEnrichAllClickRef,
              onDeduplicateClickRef,
              addColumnInProgressRef,
              addRowInProgressRef,
              showSnackbarRef,
              headerStateRef,
              onBumpSpreadsheetDataVersionRef
            });

            registerUniverEvents(univerAPI, {
              campaignId,
              viewType,
              canEditRef,
              showSnackbarRef,
              showSheetLockedModalRef,
              prospectPrioritizationActiveRef,
              headerStateRef,
              addColumnInProgressRef,
              addRowInProgressRef,
              validationErrorsMapRef,
              setValidationErrorsRef,
              requestSheetRepaint,
              onColumnsChangedRef,
              syncDebounceTimerRef,
              scheduleDebouncedSyncRef,
              dbTriggeredSyncPendingRef,
              syncBufferRef,
              rowOperationPromiseRef,
              columnOperationPromiseRef,
              isApplyingValidationStylesRef,
              isApplyingEnrichmentCellRef,
              isApplyingSyncFromBackendRef,
              enrichmentLockedRef,
              skipInsertColBeforeRef,
              skipInsertColCreateRef,
              applyBackendColumnReorderInProgressRef,
              syncOnCellEditDebounceMs: SYNC_ON_CELL_EDIT_DEBOUNCE_MS,
              getColTypeFromSchema,
              normalizeCellValue,
              cellValueToUniverCell,
              ensureDomainForRowsInPayload: ensureDomainForRows,
              syncSheetCellsToBackend,
              normalizeColumnIndices,
              onBackendOnlyColumns,
              applySheetColumnOrderFromBackend,
              enrichingCellsRef,
              setEnrichingCellsCount,
              prioritizingCellsRef,
              setPrioritizingCellsCount,
              spreadsheetBackendSyncPhaseSetterRef
            });

            const maxRowId = cellsLimited.reduce(
              (max, c) => Math.max(max, c.row_id),
              -1
            );
            const rowCount = Math.min(
              MAX_SPREADSHEET_ROWS,
              Math.max(maxRowId + 2, 2)
            );

            const columnData = computeColumnWidths(
              columnNamesByIndex,
              cells,
              columns
            );

            univer.createUnit(UniverInstanceType.UNIVER_SHEET, {
              sheets: {
                'sheet-01': {
                  columnCount: columnNamesByIndex.length,
                  columnHeader: {
                    height: 24
                  },
                  rowCount,
                  cellData: { ...univerCells },
                  columnData
                }
              }
            });

            const columnHeaderColumnsCfg = buildColumnHeaderColumnsCfg(
              columnNamesByIndex,
              primaryColumnHeaderBgRef.current,
              {
                spreadsheetViewType: spreadsheetViewTypeForHeaderRef.current,
                leadPrimaryMatchAccountsChromeBg:
                  campaignChromeAccountBgForLeadPrimaryRef.current,
                prioritizationColumnHeaderBg:
                  prioritizationColumnHeaderBgForHeaderRef.current
              }
            );

            const {
              runSetup,
              faviconColumnCustomRender,
              validationErrorHighlightRender,
              enrichingCellsHighlightRender
            } = createSheetSetupAndRenders({
              univerAPI,
              headerStateRef,
              themePrimary,
              columnHeaderColumnsCfg,
              columnNamesByIndex,
              columnData,
              columnHeaderExtensionDisposableRef,
              setSheetVisibleRef,
              setCellsLoading,
              getValidationErrorsMap: () => validationErrorsMapRef.current,
              getExecutingCellKeys: () =>
                executingCellKeysReadonlyRef.current!,
              invalidCellStyle: INVALID_CELL_STYLE
            });

            registerCellRenderOnLifecycle({
              univerAPI:
                univerAPI as RegisterCellRenderOnLifecycleOptions['univerAPI'],
              cellRenders: [
                faviconColumnCustomRender,
                validationErrorHighlightRender,
                enrichingCellsHighlightRender
              ],
              setFaviconLoadedCallback,
              requestRepaint: requestSheetRepaint,
              runSetup,
              cellRenderDisposableRef
            });

            headerStateRef.current.refreshHeaderBar = () => {
              const state = headerStateRef.current;
              if (!state) return;
              const fWorksheet = univerAPI
                .getActiveWorkbook()
                ?.getActiveSheet();
              if (
                fWorksheet &&
                typeof fWorksheet.customizeColumnHeader === 'function'
              ) {
                const columnsCfg = buildColumnHeaderColumnsCfg(
                  state.columnNamesByIndex,
                  primaryColumnHeaderBgRef.current,
                  {
                    spreadsheetViewType:
                      spreadsheetViewTypeForHeaderRef.current,
                    leadPrimaryMatchAccountsChromeBg:
                      campaignChromeAccountBgForLeadPrimaryRef.current,
                    prioritizationColumnHeaderBg:
                      prioritizationColumnHeaderBgForHeaderRef.current
                  }
                );
                fWorksheet.customizeColumnHeader({ columnsCfg });
                fWorksheet.refreshCanvas?.();
              }
            };
            applyColumnNameOverridesToHeaderState(
              headerStateRef.current,
              columnNameOverridesRef.current
            );
            applyColumnSchemaOverridesToHeaderState(
              headerStateRef.current,
              columnSchemaOverridesRef.current
            );

            const openHeaderEditorForColumn = (col: number) => {
              const info = getColumnEditorInfoForColumn(
                col,
                headerStateRef.current
              );
              if (info) onColumnHeaderClickRef.current?.(info);
            };
            const openHeaderEditor = (params: unknown) => {
              const p = params as { column: number };
              openHeaderEditorForColumn(p.column);
            };
            if ('ColumnHeaderClick' in univerAPI.Event) {
              univerAPI.addEvent(
                univerAPI.Event
                  .ColumnHeaderClick as keyof typeof univerAPI.Event,
                openHeaderEditor
              );
            }
            if ('CellClicked' in univerAPI.Event) {
              univerAPI.addEvent(
                univerAPI.Event.CellClicked as keyof typeof univerAPI.Event,
                createSpreadsheetCellClickedHandler({
                  onColumnDeselect: () => onColumnDeselectRef.current?.()
                })
              );
            }
          }
        )
        .catch(() => {
          if (!cancelled) setCellsLoading(false);
        });
      return () => {
        cancelled = true;
        columnHeaderExtensionDisposableRef.current?.dispose();
        columnHeaderExtensionDisposableRef.current = null;
      };
    }, [
      campaignId,
      spreadsheetDataVersion,
      viewType,
      applySpreadsheetBackendSyncPhase
    ]);

    useEffect(() => {
      return () => {
        enrichmentStreamCancelRef.current?.();
        enrichmentStreamCancelRef.current = null;
        setActiveEnrichmentWorkflowId(null);
        enrichingCellsRef.current.clear();
        setEnrichingCellsCount(0);
        prioritizationStreamCancelRef.current?.();
        prioritizationStreamCancelRef.current = null;
        setActivePrioritizationWorkflowId(null);
        setPrioritizationSheetLock(false);
        prioritizingCellsRef.current.clear();
        setPrioritizingCellsCount(0);
      };
    }, [campaignId]);

    // Clear sync debounce and operation chains on campaign change or unmount; flush one final sync to avoid data loss
    useEffect(() => {
      rowOperationPromiseRef.current = Promise.resolve();
      columnOperationPromiseRef.current = Promise.resolve();
      return () => {
        if (syncDebounceTimerRef.current != null) {
          clearTimeout(syncDebounceTimerRef.current);
          syncDebounceTimerRef.current = null;
        }
        const state = headerStateRef.current;
        const api = univerAPIRef.current;
        if (state?.campaignId && api) {
          spreadsheetBackendSyncPhaseSetterRef.current?.('saving');
          const options = {
            viewType,
            columns: state.columns,
            syncBufferRef,
            isApplyingSyncFromBackendRef,
            enrichmentLockedRef,
            cellValueToUniverCell
          };
          syncSheetCellsToBackend(
            state.campaignId,
            api,
            state.columnNamesByIndex,
            options
          )
            .catch(() => {})
            .finally(() => {
              spreadsheetBackendSyncPhaseSetterRef.current?.('idle');
            });
        }
        rowOperationPromiseRef.current = Promise.resolve();
        columnOperationPromiseRef.current = Promise.resolve();
      };
    }, [campaignId, viewType]);

    useEffect(() => {
      return () => {
        if (savedToIdleTimerRef.current != null) {
          clearTimeout(savedToIdleTimerRef.current);
          savedToIdleTimerRef.current = null;
        }
      };
    }, []);

    // Warn and attempt save when user leaves with pending sync (e.g. closes tab before debounce fires)
    useEffect(() => {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        if (syncDebounceTimerRef.current == null) return;
        // Pending unsaved changes: show browser "Leave site?" dialog
        e.preventDefault();
        (e as BeforeUnloadEvent & { returnValue: string }).returnValue = '';
        // Also flush immediately so if user still leaves, we tried to save (may not complete)
        clearTimeout(syncDebounceTimerRef.current);
        syncDebounceTimerRef.current = null;
        const state = headerStateRef.current;
        const api = univerAPIRef.current;
        if (state?.campaignId && api) {
          const options = {
            viewType,
            columns: state.columns,
            syncBufferRef,
            isApplyingSyncFromBackendRef,
            enrichmentLockedRef,
            cellValueToUniverCell
          };
          syncSheetCellsToBackend(
            state.campaignId,
            api,
            state.columnNamesByIndex,
            options
          ).catch(() => {});
        }
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () =>
        window.removeEventListener('beforeunload', handleBeforeUnload);
    }, []);

    // Prevent two-finger horizontal swipe from triggering browser back/forward while spreadsheet is mounted
    useEffect(() => {
      const html = document.documentElement;
      const body = document.body;
      const prevHtmlOverscroll = html.style.overscrollBehaviorX;
      const prevBodyOverscroll = body.style.overscrollBehaviorX;
      html.style.overscrollBehaviorX = 'none';
      body.style.overscrollBehaviorX = 'none';
      return () => {
        html.style.overscrollBehaviorX = prevHtmlOverscroll;
        body.style.overscrollBehaviorX = prevBodyOverscroll;
      };
    }, []);

    useEffect(() => {
      const state = headerStateRef.current;
      if (!state) return;
      applyColumnNameOverridesToHeaderState(state, columnNameOverrides);
    }, [columnNameOverrides]);

    useEffect(() => {
      const state = headerStateRef.current;
      applyColumnSchemaOverridesToHeaderState(state, columnSchemaOverrides);
      state?.refreshHeaderBar?.();
      reapplyValidationStylesForColumnOverrides({
        api: univerAPIRef.current,
        headerStateRef,
        columnSchemaOverridesRef,
        validationErrorsMapRef,
        setValidationErrorsRef,
        isApplyingValidationStylesRef,
        requestSheetRepaint,
        getColTypeFromSchema,
        normalizeCellValue,
        cellValueToUniverCell
      });
    }, [columnSchemaOverrides]);

    const enrichmentOverlayActive =
      !!activeEnrichmentWorkflowId || enrichmentSheetLock;
    const prioritizationOverlayActive =
      !!activePrioritizationWorkflowId || prioritizationSheetLock;
    const overlayVariant: EnrichmentOverlayVariant =
      enrichmentOverlayActive && prioritizationOverlayActive
        ? 'both'
        : prioritizationOverlayActive
          ? 'prioritization'
          : 'enrichment';
    const overlayCellCount =
      (enrichmentOverlayActive ? enrichingCellsCount : 0) +
      (prioritizationOverlayActive ? prioritizingCellsCount : 0);

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0
        }}
      >
        <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
          {cellsLoading && (
            <MuiBox
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LoadingComponent message="Loading spreadsheet cells..." />
            </MuiBox>
          )}
          <Box
            sx={{
              height: '100%',
              width: '100%',
              minHeight: 0,
              opacity: sheetVisible ? 1 : 0
            }}
          >
            <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
              <div
                ref={univerContainerRef}
                style={{ height: '100%', width: '100%' }}
              />
              {(enrichmentOverlayActive || prioritizationOverlayActive) && (
                <EnrichmentOverlay
                  variant={overlayVariant}
                  cellCount={overlayCellCount}
                />
              )}
            </Box>
          </Box>
        </Box>
        {validationErrors.length > 0 && (
          <Box
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'grey.50',
              px: 1.5,
              py: validationErrorsPanelVisible ? 1 : 0
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Validation errors ({validationErrors.length})
              </Typography>
              <IconButton
                size="small"
                aria-label={
                  validationErrorsPanelVisible
                    ? 'Hide validation errors'
                    : 'Show validation errors'
                }
                onClick={() => setValidationErrorsPanelVisible((v) => !v)}
                sx={{ flexShrink: 0 }}
              >
                {validationErrorsPanelVisible ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            </Box>
            {validationErrorsPanelVisible && (
              <Box
                component="ul"
                sx={{
                  m: 0,
                  mt: 0.5,
                  pl: 2.5,
                  typography: 'body2',
                  maxHeight: 120,
                  overflow: 'auto'
                }}
              >
                {validationErrors.map((e, i) => (
                  <Box component="li" key={`${e.row}-${e.col}-${i}`}>
                    Row {e.row + 1}, &quot;{displayColumnName(e.columnName)}
                    &quot;: {e.message}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
        <EnrichmentConfirmationDialog
          open={enrichmentModalOpen}
          onClose={enrichmentHandlers.handleEnrichmentModalClose}
          pending={enrichmentPending}
          onConfirm={enrichmentHandlers.handleEnrichmentConfirm}
        />
        <SpreadsheetLockedDialog
          open={sheetLockedModalOpen}
          onClose={() => setSheetLockedModalOpen(false)}
        />
      </Box>
    );
  }
);

export default Spreadsheet;
